/**
 * Shared types, constants, and reducer for the F5 contribute-piece form.
 *
 * The state shape mirrors the eventual `POST /pieces/contribute` payload (Story 5),
 * so subsequent stories (sources, preview checklist, autosave, submit) can read and
 * write through it without reshaping.
 */

export const AKA_MAX_ENTRIES = 10;
export const SUMMARY_MAX_LENGTH = 240;
export const ABC_MAX_LENGTH = 8192;
export const COMPOSER_YEAR_MAX_LENGTH = 4;

export const CONTRIBUTE_FORM_OPTIONS = [
  'Reel',
  'Jig',
  'Hornpipe',
  'Slip Jig',
  'Polka',
  'Slide',
  'Air',
  'March',
  'Waltz',
  'Strathspey',
  'Mazurka',
  'Other',
] as const;
export type ContributeFormOption = (typeof CONTRIBUTE_FORM_OPTIONS)[number];

export const CONTRIBUTE_REGION_OPTIONS = [
  { value: 'ireland', label: 'Ireland' },
  { value: 'scotland', label: 'Scotland' },
  { value: 'cape_breton', label: 'Cape Breton' },
  { value: 'brittany', label: 'Brittany' },
  { value: 'england', label: 'England' },
  { value: 'galicia', label: 'Galicia' },
  { value: 'asturias', label: 'Asturias' },
  { value: 'other', label: 'Other' },
] as const;
export type ContributeRegion = (typeof CONTRIBUTE_REGION_OPTIONS)[number]['value'];

export const CONTRIBUTE_STRUCTURE_PRESETS = [
  'AABB',
  'AABBCC',
  'ABAB',
  'ABCABC',
  'Through-composed',
] as const;

export const CONTRIBUTE_COMPOSER_MODES = ['traditional', 'named', 'unknown'] as const;
export type ContributeComposerMode = (typeof CONTRIBUTE_COMPOSER_MODES)[number];

// Lower-case in form state; submit-time mapping (Story 13) up-cases to match the
// backend `PieceSourceKind` enum (URL, BOOK, RECORDING, YOUTUBE, SPOTIFY).
export const CONTRIBUTE_SOURCE_KINDS = ['url', 'book', 'recording', 'youtube', 'spotify'] as const;
export type ContributeSourceKind = (typeof CONTRIBUTE_SOURCE_KINDS)[number];

export const SOURCE_VALUE_MAX_LENGTH = 2000;
export const SOURCE_NOTE_MAX_LENGTH = 500;

export type ContributeSourceDraft = {
  draftId: string;
  kind: ContributeSourceKind;
  value: string;
  note: string;
};

export type ContributeSettingDraft = {
  draftId: string;
  name: string;
  keyId: string | null;
  structureLabel: string;
  abcNotation: string;
  isPrimary: boolean;
  // Sheet-music PDF attached during the contribute flow (#340). Two-phase upload:
  // posted to /arrangements/{id}/resources/upload after the piece is created. Not
  // serialised to localStorage (File instances aren't JSON-safe).
  pendingFile: File | null;
  // Audio recording attached during the contribute flow (#341). Independent slot:
  // contributors can attach both a PDF and an audio recording to the same setting.
  pendingAudioFile: File | null;
};

export type ContributeFormState = {
  title: string;
  aka: string[];
  forms: ContributeFormOption[];
  composerMode: ContributeComposerMode | null;
  composerName: string;
  composerYear: string;
  region: ContributeRegion | null;
  summary: string;
  settings: ContributeSettingDraft[];
  sources: ContributeSourceDraft[];
};

export type ContributeFormAction =
  | { type: 'SET_TITLE'; title: string }
  | { type: 'ADD_AKA'; value: string }
  | { type: 'REMOVE_AKA'; index: number }
  | { type: 'TOGGLE_FORM'; form: ContributeFormOption }
  | { type: 'SET_COMPOSER_MODE'; mode: ContributeComposerMode }
  | { type: 'SET_COMPOSER_NAME'; name: string }
  | { type: 'SET_COMPOSER_YEAR'; year: string }
  | { type: 'SET_REGION'; region: ContributeRegion }
  | { type: 'SET_SUMMARY'; summary: string }
  | { type: 'ADD_SETTING' }
  | { type: 'REMOVE_SETTING'; draftId: string }
  | { type: 'UPDATE_SETTING'; draftId: string; patch: Partial<Omit<ContributeSettingDraft, 'draftId' | 'isPrimary' | 'pendingFile' | 'pendingAudioFile'>> }
  | { type: 'MARK_SETTING_PRIMARY'; draftId: string }
  | { type: 'SET_SETTING_FILE'; draftId: string; file: File | null }
  | { type: 'SET_SETTING_AUDIO_FILE'; draftId: string; file: File | null }
  | { type: 'ADD_SOURCE'; kind: ContributeSourceKind }
  | { type: 'REMOVE_SOURCE'; draftId: string }
  | {
      type: 'UPDATE_SOURCE';
      draftId: string;
      patch: Partial<Pick<ContributeSourceDraft, 'value' | 'note'>>;
    };

/**
 * `JSON.stringify` replacer that strips pending File slots from each setting before
 * the form state is persisted to localStorage. A serialised File would round-trip
 * as `{}` and leave a phantom truthy reference after a reload.
 */
export function contributeFormStorageReplacer(key: string, value: unknown): unknown {
  if (key === 'pendingFile' || key === 'pendingAudioFile') return null;
  return value;
}

function newDraftId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function emptySetting(isPrimary: boolean, name = ''): ContributeSettingDraft {
  return {
    draftId: newDraftId(),
    name,
    keyId: null,
    structureLabel: '',
    abcNotation: '',
    isPrimary,
    pendingFile: null,
    pendingAudioFile: null,
  };
}

export function initialContributeFormState(seed: { title?: string }): ContributeFormState {
  return {
    title: seed.title ?? '',
    aka: [],
    forms: [],
    composerMode: null,
    composerName: '',
    composerYear: '',
    region: null,
    summary: '',
    settings: [emptySetting(true)],
    sources: [],
  };
}

function emptySource(kind: ContributeSourceKind): ContributeSourceDraft {
  return {
    draftId: newDraftId(),
    kind,
    value: '',
    note: '',
  };
}

function sanitiseYear(value: string): string {
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.slice(0, COMPOSER_YEAR_MAX_LENGTH);
}

function patchSettingAbc(patch: ContributeSettingDraft, abc: string): ContributeSettingDraft {
  return { ...patch, abcNotation: abc.slice(0, ABC_MAX_LENGTH) };
}

export function contributeFormReducer(
  state: ContributeFormState,
  action: ContributeFormAction,
): ContributeFormState {
  switch (action.type) {
    case 'SET_TITLE':
      return { ...state, title: action.title };

    case 'ADD_AKA': {
      const trimmed = action.value.trim();
      if (trimmed.length === 0) return state;
      const exists = state.aka.some((v) => v.toLowerCase() === trimmed.toLowerCase());
      if (exists) return state;
      if (state.aka.length >= AKA_MAX_ENTRIES) return state;
      return { ...state, aka: [...state.aka, trimmed] };
    }

    case 'REMOVE_AKA':
      return { ...state, aka: state.aka.filter((_, i) => i !== action.index) };

    case 'TOGGLE_FORM': {
      const has = state.forms.includes(action.form);
      return {
        ...state,
        forms: has ? state.forms.filter((f) => f !== action.form) : [...state.forms, action.form],
      };
    }

    case 'SET_COMPOSER_MODE': {
      if (action.mode === 'named') {
        return { ...state, composerMode: 'named' };
      }
      return { ...state, composerMode: action.mode, composerName: '', composerYear: '' };
    }

    case 'SET_COMPOSER_NAME':
      return { ...state, composerName: action.name };

    case 'SET_COMPOSER_YEAR':
      return { ...state, composerYear: sanitiseYear(action.year) };

    case 'SET_REGION':
      return { ...state, region: state.region === action.region ? null : action.region };

    case 'SET_SUMMARY':
      return { ...state, summary: action.summary.slice(0, SUMMARY_MAX_LENGTH) };

    case 'ADD_SETTING':
      return { ...state, settings: [...state.settings, emptySetting(false, state.title)] };

    case 'REMOVE_SETTING': {
      if (state.settings.length <= 1) return state;
      const removingPrimary = state.settings.find((s) => s.draftId === action.draftId)?.isPrimary;
      const remaining = state.settings.filter((s) => s.draftId !== action.draftId);
      if (removingPrimary && remaining.length > 0) {
        remaining[0] = { ...remaining[0], isPrimary: true };
      }
      return { ...state, settings: remaining };
    }

    case 'UPDATE_SETTING':
      return {
        ...state,
        settings: state.settings.map((s) => {
          if (s.draftId !== action.draftId) return s;
          let next = { ...s, ...action.patch };
          if (typeof action.patch.abcNotation === 'string') {
            next = patchSettingAbc(next, action.patch.abcNotation);
          }
          return next;
        }),
      };

    case 'MARK_SETTING_PRIMARY':
      return {
        ...state,
        settings: state.settings.map((s) => ({ ...s, isPrimary: s.draftId === action.draftId })),
      };

    case 'SET_SETTING_FILE':
      return {
        ...state,
        settings: state.settings.map((s) =>
          s.draftId === action.draftId ? { ...s, pendingFile: action.file } : s,
        ),
      };

    case 'SET_SETTING_AUDIO_FILE':
      return {
        ...state,
        settings: state.settings.map((s) =>
          s.draftId === action.draftId ? { ...s, pendingAudioFile: action.file } : s,
        ),
      };

    case 'ADD_SOURCE':
      return { ...state, sources: [...state.sources, emptySource(action.kind)] };

    case 'REMOVE_SOURCE':
      return { ...state, sources: state.sources.filter((s) => s.draftId !== action.draftId) };

    case 'UPDATE_SOURCE':
      return {
        ...state,
        sources: state.sources.map((s) => {
          if (s.draftId !== action.draftId) return s;
          const next = { ...s };
          if (typeof action.patch.value === 'string') {
            next.value = action.patch.value.slice(0, SOURCE_VALUE_MAX_LENGTH);
          }
          if (typeof action.patch.note === 'string') {
            next.note = action.patch.note.slice(0, SOURCE_NOTE_MAX_LENGTH);
          }
          return next;
        }),
      };

    default:
      return state;
  }
}
